// src/pages/CreateTokenPage.tsx
import React, { useState } from 'react';
import TokenCreationForm from '../components/TokenCreationForm';
import PumpfunCreationForm from '../components/PumpfunCreationForm';
import RaydiumCreationForm from '../components/RaydiumCreationForm';
import BonkCreationForm from '../components/BonkCreationForm';
import { TrendingUp, Zap, Rocket, Droplet } from 'lucide-react';

type Platform = 'bonk' | 'pumpfun' | 'raydium' | 'revshare';

const CreateTokenPage: React.FC = () => {
  const [selectedPlatform, setSelectedPlatform] = useState<Platform>('bonk');

  return (
    <div className="max-w-6xl mx-auto">
      <div className="mb-8 text-center">
        <h1 className="text-4xl font-bold mb-3 bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
          Create Your Token
        </h1>
        <p className="text-gray-400">Choose your preferred launch platform</p>
      </div>

      
      {/* Platform Selector Tabs */}
      <div className="hidden sm:flex space-x-2 mb-8">
        <button
          onClick={() => setSelectedPlatform('bonk')}
          className={`flex-1 py-3 px-4 rounded-xl font-medium transition-all flex items-center justify-center space-x-2 ${
            selectedPlatform === 'bonk'
              ? 'bg-gradient-to-r from-orange-600 to-yellow-600 shadow-lg shadow-orange-500/20'
              : 'bg-gray-800/50 border border-gray-700 hover:bg-gray-800'
          }`}
        >
          <TrendingUp className="w-5 h-5" />
          <span>LetsBonk.fun</span>
          <span className="text-xs opacity-80 hidden sm:inline">(Community)</span>
        </button>

        <button
          onClick={() => setSelectedPlatform('pumpfun')}
          className={`flex-1 py-3 px-4 rounded-xl font-medium transition-all flex items-center justify-center space-x-2 ${
            selectedPlatform === 'pumpfun'
              ? 'bg-gradient-to-r from-green-600 to-emerald-600 shadow-lg shadow-green-500/20'
              : 'bg-gray-800/50 border border-gray-700 hover:bg-gray-800'
          }`}
        >
          <TrendingUp className="w-5 h-5" />
          <span>Pump.fun</span>
          <span className="text-xs opacity-80 hidden sm:inline">(Fair Launch)</span>
        </button>

        <button
          onClick={() => setSelectedPlatform('raydium')}
          className={`flex-1 py-3 px-4 rounded-xl font-medium transition-all flex items-center justify-center space-x-2 ${
            selectedPlatform === 'raydium'
              ? 'bg-gradient-to-r from-blue-600 to-cyan-600 shadow-lg shadow-blue-500/20'
              : 'bg-gray-800/50 border border-gray-700 hover:bg-gray-800'
          }`}
        >
          <Droplet className="w-5 h-5" />
          <span>Raydium</span>
          <span className="text-xs opacity-80 hidden sm:inline">(Pro DEX)</span>
        </button>
        
        <button
          onClick={() => setSelectedPlatform('revshare')}
          className={`flex-1 py-3 px-4 rounded-xl font-medium transition-all flex items-center justify-center space-x-2 ${
            selectedPlatform === 'revshare'
              ? 'bg-gradient-to-r from-purple-600 to-pink-600 shadow-lg shadow-purple-500/20'
              : 'bg-gray-800/50 border border-gray-700 hover:bg-gray-800'
          }`}
        >
          <Zap className="w-5 h-5" />
          <span>RevShare</span>
          <span className="text-xs opacity-80 hidden sm:inline">(Revenue)</span>
        </button>
      </div>

      {/* Platform Comparison */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {/* LetsBonk.fun Card */}
        <div className="space-y-2">
          <button
            onClick={() => setSelectedPlatform('bonk')}
            className={`w-full sm:hidden py-3 px-4 rounded-xl font-medium transition-all flex items-center justify-center space-x-2 ${
              selectedPlatform === 'bonk'
                ? 'bg-gradient-to-r from-orange-600 to-yellow-600 shadow-lg shadow-orange-500/20'
                : 'bg-gray-800/50 border border-gray-700 hover:bg-gray-800'
            }`}
          >
            <TrendingUp className="w-5 h-5" />
            <span>LetsBonk.fun</span>
          </button>
          <div className={`p-4 rounded-xl border-2 transition-all ${
            selectedPlatform === 'bonk'
              ? 'border-orange-500 bg-orange-900/10'
              : 'border-gray-700 bg-gray-800/30'
          }`}>
            <h3 className="font-semibold mb-2 flex items-center space-x-2 sm:flex hidden">
              <TrendingUp className="w-4 h-4 text-orange-400" />
              <span>LetsBonk.fun</span>
            </h3>
            <ul className="text-xs text-gray-400 space-y-1">
              <li className="flex items-start">
                <span className="text-orange-400 mr-1">✓</span>
                <span>Raydium LaunchLab powered</span>
              </li>
              <li className="flex items-start">
                <span className="text-orange-400 mr-1">✓</span>
                <span>Custom bonding curves</span>
              </li>
              <li className="flex items-start">
                <span className="text-orange-400 mr-1">✓</span>
                <span>BONK ecosystem benefits</span>
              </li>
              <li className="flex items-start">
                <span className="text-orange-400 mr-1">✓</span>
                <span>Revenue sharing (CPMM)</span>
              </li>
              <li className="flex items-start">
                <span className="text-orange-400 mr-1">✓</span>
                <span>~0.01 SOL fee</span>
              </li>
            </ul>
            <div className="mt-3 pt-3 border-t border-gray-700">
              <div className="text-xs text-gray-500">Best for:</div>
              <div className="text-xs font-medium text-orange-400">Community tokens</div>
            </div>
          </div>
        </div>

        {/* Pump.fun Card */}
        <div className="space-y-2">
          <button
            onClick={() => setSelectedPlatform('pumpfun')}
            className={`w-full sm:hidden py-3 px-4 rounded-xl font-medium transition-all flex items-center justify-center space-x-2 ${
              selectedPlatform === 'pumpfun'
                ? 'bg-gradient-to-r from-green-600 to-emerald-600 shadow-lg shadow-green-500/20'
                : 'bg-gray-800/50 border border-gray-700 hover:bg-gray-800'
            }`}
          >
            <TrendingUp className="w-5 h-5" />
            <span>Pump.fun</span>
          </button>
          <div className={`p-4 rounded-xl border-2 transition-all ${
            selectedPlatform === 'pumpfun'
              ? 'border-green-500 bg-green-900/10'
              : 'border-gray-700 bg-gray-800/30'
          }`}>
            <h3 className="font-semibold mb-2 flex items-center space-x-2 sm:flex hidden">
              <TrendingUp className="w-4 h-4 text-green-400" />
              <span>Pump.fun</span>
            </h3>
            <ul className="text-xs text-gray-400 space-y-1">
              <li className="flex items-start">
                <span className="text-green-400 mr-1">✓</span>
                <span>Bonding curve pricing</span>
              </li>
              <li className="flex items-start">
                <span className="text-green-400 mr-1">✓</span>
                <span>No team allocation</span>
              </li>
              <li className="flex items-start">
                <span className="text-green-400 mr-1">✓</span>
                <span>Auto-migration at $69k</span>
              </li>
              <li className="flex items-start">
                <span className="text-green-400 mr-1">✓</span>
                <span>Fair launch mechanism</span>
              </li>
              <li className="flex items-start">
                <span className="text-green-400 mr-1">✓</span>
                <span>0.02 SOL fee</span>
              </li>
            </ul>
            <div className="mt-3 pt-3 border-t border-gray-700">
              <div className="text-xs text-gray-500">Best for:</div>
              <div className="text-xs font-medium text-green-400">Meme coins & fair launches</div>
            </div>
          </div>
        </div>

        {/* Raydium Card */}
        <div className="space-y-2">
          <button
            onClick={() => setSelectedPlatform('raydium')}
            className={`w-full sm:hidden py-3 px-4 rounded-xl font-medium transition-all flex items-center justify-center space-x-2 ${
              selectedPlatform === 'raydium'
                ? 'bg-gradient-to-r from-blue-600 to-cyan-600 shadow-lg shadow-blue-500/20'
                : 'bg-gray-800/50 border border-gray-700 hover:bg-gray-800'
            }`}
          >
            <Droplet className="w-5 h-5" />
            <span>Raydium</span>
          </button>
          <div className={`p-4 rounded-xl border-2 transition-all ${
            selectedPlatform === 'raydium'
              ? 'border-blue-500 bg-blue-900/10'
              : 'border-gray-700 bg-gray-800/30'
          }`}>
            <h3 className="font-semibold mb-2 flex items-center space-x-2 sm:flex hidden">
              <Droplet className="w-4 h-4 text-blue-400" />
              <span>Raydium</span>
            </h3>
            <ul className="text-xs text-gray-400 space-y-1">
              <li className="flex items-start">
                <span className="text-blue-400 mr-1">✓</span>
                <span>Professional DEX</span>
              </li>
              <li className="flex items-start">
                <span className="text-blue-400 mr-1">✓</span>
                <span>Deep liquidity pools</span>
              </li>
              <li className="flex items-start">
                <span className="text-blue-400 mr-1">✓</span>
                <span>Multiple pool types</span>
              </li>
              <li className="flex items-start">
                <span className="text-blue-400 mr-1">✓</span>
                <span>Burn & Earn feature</span>
              </li>
              <li className="flex items-start">
                <span className="text-blue-400 mr-1">✓</span>
                <span>From 0.15 SOL fee</span>
              </li>
            </ul>
            <div className="mt-3 pt-3 border-t border-gray-700">
              <div className="text-xs text-gray-500">Best for:</div>
              <div className="text-xs font-medium text-blue-400">Serious projects & DeFi</div>
            </div>
          </div>
        </div>
        
        {/* RevShare Card */}
        <div className="space-y-2">
          <button
            onClick={() => setSelectedPlatform('revshare')}
            className={`w-full sm:hidden py-3 px-4 rounded-xl font-medium transition-all flex items-center justify-center space-x-2 ${
              selectedPlatform === 'revshare'
                ? 'bg-gradient-to-r from-purple-600 to-pink-600 shadow-lg shadow-purple-500/20'
                : 'bg-gray-800/50 border border-gray-700 hover:bg-gray-800'
            }`}
          >
            <Zap className="w-5 h-5" />
            <span>RevShare</span>
          </button>
          <div className={`p-4 rounded-xl border-2 transition-all ${
            selectedPlatform === 'revshare'
              ? 'border-purple-500 bg-purple-900/10'
              : 'border-gray-700 bg-gray-800/30'
          }`}>
            <h3 className="font-semibold mb-2 flex items-center space-x-2 sm:flex hidden">
              <Zap className="w-4 h-4 text-purple-400" />
              <span>RevShare</span>
            </h3>
            <ul className="text-xs text-gray-400 space-y-1">
              <li className="flex items-start">
                <span className="text-purple-400 mr-1">✓</span>
                <span>Revenue sharing model</span>
              </li>
              <li className="flex items-start">
                <span className="text-purple-400 mr-1">✓</span>
                <span>Fixed 1B supply</span>
              </li>
              <li className="flex items-start">
                <span className="text-purple-400 mr-1">✓</span>
                <span>Transfer fee distribution</span>
              </li>
              <li className="flex items-start">
                <span className="text-purple-400 mr-1">✓</span>
                <span>Multiple reward modes</span>
              </li>
              <li className="flex items-start">
                <span className="text-purple-400 mr-1">✓</span>
                <span>0.05 SOL fee</span>
              </li>
            </ul>
            <div className="mt-3 pt-3 border-t border-gray-700">
              <div className="text-xs text-gray-500">Best for:</div>
              <div className="text-xs font-medium text-purple-400">Community rewards</div>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Comparison Table */}
      <div className="mb-8 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-700">
              <th className="text-left py-2 px-3">Feature</th>
              <th className="text-center py-2 px-3">
                <div className="flex items-center justify-center space-x-1">
                  <TrendingUp className="w-3 h-3 text-orange-400" />
                  <span>LetsBonk</span>
                </div>
              </th>
              <th className="text-center py-2 px-3">
                <div className="flex items-center justify-center space-x-1">
                  <TrendingUp className="w-3 h-3 text-green-400" />
                  <span>Pump.fun</span>
                </div>
              </th>
              <th className="text-center py-2 px-3">
                <div className="flex items-center justify-center space-x-1">
                  <Droplet className="w-3 h-3 text-blue-400" />
                  <span>Raydium</span>
                </div>
              </th>
              <th className="text-center py-2 px-3">
                <div className="flex items-center justify-center space-x-1">
                  <Zap className="w-3 h-3 text-purple-400" />
                  <span>RevShare</span>
                </div>
              </th>
            </tr>
          </thead>
          <tbody className="text-xs">
            <tr className="border-b border-gray-800">
              <td className="py-2 px-3 text-gray-400">Creation Cost</td>
              <td className="text-center py-2 px-3">~0.01 SOL</td>
              <td className="text-center py-2 px-3">0.02 SOL</td>
              <td className="text-center py-2 px-3">0.15+ SOL</td>
              <td className="text-center py-2 px-3">0.05 SOL</td>
            </tr>
            <tr className="border-b border-gray-800">
              <td className="py-2 px-3 text-gray-400">Liquidity Lock</td>
              <td className="text-center py-2 px-3">Auto</td>
              <td className="text-center py-2 px-3">Auto</td>
              <td className="text-center py-2 px-3">Optional</td>
              <td className="text-center py-2 px-3">N/A</td>
            </tr>
            <tr className="border-b border-gray-800">
              <td className="py-2 px-3 text-gray-400">Trading Fees</td>
              <td className="text-center py-2 px-3">1%</td>
              <td className="text-center py-2 px-3">1%</td>
              <td className="text-center py-2 px-3">0.01-1%</td>
              <td className="text-center py-2 px-3">Variable</td>
            </tr>
            <tr className="border-b border-gray-800">
              <td className="py-2 px-3 text-gray-400">Best For</td>
              <td className="text-center py-2 px-3 text-orange-400">Community</td>
              <td className="text-center py-2 px-3 text-green-400">Memes</td>
              <td className="text-center py-2 px-3 text-blue-400">DeFi</td>
              <td className="text-center py-2 px-3 text-purple-400">Revenue</td>
            </tr>
            <tr className="border-b border-gray-800">
              <td className="py-2 px-3 text-gray-400">Complexity</td>
              <td className="text-center py-2 px-3">
                <span className="text-blue-400">Moderate</span>
              </td>
              <td className="text-center py-2 px-3">
                <span className="text-green-400">Simple</span>
              </td>
              <td className="text-center py-2 px-3">
                <span className="text-yellow-400">Advanced</span>
              </td>
              <td className="text-center py-2 px-3">
                <span className="text-blue-400">Moderate</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Selected Platform Form */}
      <div className="transition-all duration-300">
        {selectedPlatform === 'bonk' && <BonkCreationForm />}
        {selectedPlatform === 'pumpfun' && <PumpfunCreationForm />}
        {selectedPlatform === 'raydium' && <RaydiumCreationForm />}
        {selectedPlatform === 'revshare' && <TokenCreationForm />}
      </div>
    </div>
  );
};

export default CreateTokenPage;